import UPNG from 'upng-js';
import { mountImageBatch } from './image-batch-queue.js';
import { canvasToBlob, imageExtension, imageIsAr, imageName, makeCanvas, nextFrame, openBitmap } from './image-batch-utils.js';
import { trackEvent } from '../core/analytics.js';

const t = (ar, en) => imageIsAr ? ar : en;

function resolveFormat(file, requested = 'auto') {
  if (requested && requested !== 'auto') {
    const ext = imageExtension(requested);
    return { mime: requested, ext };
  }
  const nameExt = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
  if (file.type === 'image/jpeg' || nameExt === 'jpg' || nameExt === 'jpeg') {
    return { mime: 'image/jpeg', ext: 'jpg' };
  }
  if (file.type === 'image/png' || nameExt === 'png') {
    return { mime: 'image/png', ext: 'png' };
  }
  if (file.type === 'image/webp' || nameExt === 'webp') {
    return { mime: 'image/webp', ext: 'webp' };
  }
  if (file.type === 'image/avif' || nameExt === 'avif') {
    return { mime: 'image/webp', ext: 'webp' };
  }
  if (file.type && file.type.startsWith('image/')) {
    return { mime: file.type, ext: imageExtension(file.type) };
  }
  return { mime: 'image/jpeg', ext: 'jpg' };
}

let currentWatermarkBounds = null;

function drawWatermarkOnContext({ ctx, width, height, settings, isPreview = false }) {
  const isLogo = settings.wmType === 'image';
  const opacity = (Number(settings.opacity) || 50) / 100;
  const sizeRatio = (Number(settings.size) || 6) / 100;
  const pos = settings.position || 'bottom_right';
  const minDim = Math.min(width, height);
  const padding = Math.max(12, Math.round(minDim * 0.035));

  ctx.save();
  ctx.globalAlpha = opacity;

  let bounds = null;

  if (isLogo && settings.logo) {
    const logo = settings.logo;
    const logoOrigW = logo.width || logo.naturalWidth || 100;
    const logoOrigH = logo.height || logo.naturalHeight || 100;
    const logoW = Math.max(24, Math.round(minDim * sizeRatio * 2.5));
    const logoAspect = logoOrigH / logoOrigW;
    const logoH = Math.max(24, Math.round(logoW * logoAspect));

    if (pos === 'tile') {
      const rot = (Number(settings.rotation) || -25) * Math.PI / 180;
      ctx.translate(width / 2, height / 2);
      ctx.rotate(rot);
      ctx.translate(-width / 2, -height / 2);

      const diag = Math.sqrt(width * width + height * height);
      const stepX = logoW * 2.2;
      const stepY = logoH * 2.2;
      for (let y = -diag; y < diag * 2; y += stepY) {
        for (let x = -diag; x < diag * 2; x += stepX) {
          ctx.drawImage(logo, x, y, logoW, logoH);
        }
      }
      bounds = { x: 0, y: 0, width, height, isTile: true };
    } else if (pos === 'custom' && Number.isFinite(Number(settings.customX)) && Number.isFinite(Number(settings.customY))) {
      const x = Math.max(0, Math.min(width - logoW, Math.round(Number(settings.customX) * width)));
      const y = Math.max(0, Math.min(height - logoH, Math.round(Number(settings.customY) * height)));
      ctx.drawImage(logo, x, y, logoW, logoH);
      bounds = { x, y, width: logoW, height: logoH, isTile: false };
    } else {
      let x = (width - logoW) / 2;
      let y = (height - logoH) / 2;

      if (pos.includes('left')) x = padding;
      else if (pos.includes('right')) x = width - padding - logoW;

      if (pos.includes('top')) y = padding;
      else if (pos.includes('bottom')) y = height - padding - logoH;

      ctx.drawImage(logo, x, y, logoW, logoH);
      bounds = { x, y, width: logoW, height: logoH, isTile: false };
    }
  } else {
    // Text watermark
    const text = settings.text || 'Kazakan Tools';
    const fontSize = Math.max(14, Math.round(minDim * sizeRatio));
    ctx.font = `700 ${fontSize}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
    ctx.fillStyle = settings.color || '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.direction = 'ltr';

    // Drop shadow for high contrast over light & dark image areas
    ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
    ctx.shadowBlur = Math.max(3, Math.round(fontSize * 0.12));
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;

    const metrics = ctx.measureText(text);
    const textW = metrics.width;
    const textH = fontSize;

    if (pos === 'tile') {
      const rot = (Number(settings.rotation) || -25) * Math.PI / 180;
      ctx.translate(width / 2, height / 2);
      ctx.rotate(rot);
      ctx.translate(-width / 2, -height / 2);

      const stepX = textW + padding * 3.5;
      const stepY = fontSize * 3;
      const diag = Math.sqrt(width * width + height * height);

      for (let y = -diag; y < diag * 2; y += stepY) {
        for (let x = -diag; x < diag * 2; x += stepX) {
          ctx.fillText(text, x, y);
        }
      }
      bounds = { x: 0, y: 0, width, height, isTile: true };
    } else if (pos === 'custom' && Number.isFinite(Number(settings.customX)) && Number.isFinite(Number(settings.customY))) {
      const x = Math.max(0, Math.min(width - textW, Math.round(Number(settings.customX) * width)));
      const y = Math.max(fontSize / 2, Math.min(height - fontSize / 2, Math.round(Number(settings.customY) * height) + fontSize / 2));
      ctx.fillText(text, x, y);
      bounds = { x, y: y - fontSize / 2, width: textW, height: textH, isTile: false };
    } else {
      let x = (width - textW) / 2;
      let y = height / 2;

      if (pos.includes('left')) {
        x = padding;
      } else if (pos.includes('right')) {
        x = width - padding - textW;
      }

      if (pos.includes('top')) {
        y = padding + fontSize / 2;
      } else if (pos.includes('bottom')) {
        y = height - padding - fontSize / 2;
      }

      ctx.fillText(text, x, y);
      bounds = { x, y: y - fontSize / 2, width: textW, height: textH, isTile: false };
    }
  }

  ctx.restore();
  return bounds;
}

const fields = [
  {
    key: 'wmType',
    label: t('نوع العلامة المائية', 'Watermark Type'),
    type: 'segmented',
    span: 'full',
    default: 'text',
    options: [
      { value: 'text', label: t('علامة نصية مخصصة', 'Text Watermark') },
      { value: 'image', label: t('صورة / شعار (Logo)', 'Image / Logo') }
    ]
  },
  {
    key: 'text',
    label: t('النص المكتوب', 'Watermark Text'),
    type: 'text',
    default: 'Kazakan Tools',
    placeholder: t('أدخل نص العلامة المائية...', 'Enter watermark text...'),
    showIf: (s) => s.wmType !== 'image'
  },
  {
    key: 'color',
    label: t('لون النص', 'Text Color'),
    type: 'color',
    default: '#ffffff',
    showIf: (s) => s.wmType !== 'image'
  },
  {
    key: 'logo',
    label: t('رفع صورة الشعار (PNG شفاف)', 'Upload Logo Image (Transparent PNG)'),
    type: 'file',
    span: 'full',
    placeholder: t('اختر صورة الشعار من جهازك أو اسحبها هنا...', 'Choose logo image or drag & drop here...'),
    showIf: (s) => s.wmType === 'image'
  },
  {
    key: 'position',
    label: t('موقع وتوزيع العلامة المائية', 'Watermark Placement'),
    type: 'position-grid',
    span: 'full',
    default: 'bottom_right',
    hint: t('انقر على أي موضع في الشبكة للتثبيت الفوري، أو اسحب العلامة المائية بحرية مباشرة على صورة المعاينة!', 'Click any anchor to snap, or drag the watermark freely on the live preview image!')
  },
  {
    key: 'customX',
    type: 'hidden',
    default: 0.75
  },
  {
    key: 'customY',
    type: 'hidden',
    default: 0.82
  },
  {
    key: 'opacity',
    label: t('نسبة الشفافية %', 'Opacity %'),
    type: 'range',
    min: 5,
    max: 100,
    step: 5,
    default: 60,
    suffix: '%'
  },
  {
    key: 'size',
    label: t('الحجم النسبي %', 'Relative Size %'),
    type: 'range',
    min: 1,
    max: 30,
    step: 1,
    default: 8,
    suffix: '%'
  },
  {
    key: 'rotation',
    label: t('زاوية الدوران للتكرار القطري °', 'Pattern Rotation Angle °'),
    type: 'range',
    min: -90,
    max: 90,
    step: 5,
    default: -25,
    suffix: '°',
    showIf: (s) => s.position === 'tile'
  },
  {
    key: 'outputFormat',
    label: t('صيغة الإخراج والحفظ', 'Output Format'),
    type: 'select',
    default: 'auto',
    options: [
      { value: 'auto', label: t('نفس صيغة الملف الأصلية (تلقائي)', 'Original format (Auto)') },
      { value: 'image/jpeg', label: 'JPEG / JPG' },
      { value: 'image/png', label: 'PNG' },
      { value: 'image/webp', label: 'WebP' }
    ]
  },
  {
    key: 'quality',
    label: t('جودة الصورة %', 'Image Quality %'),
    type: 'range',
    min: 10,
    max: 100,
    step: 1,
    default: 100,
    suffix: '%'
  }
];

const root = document.getElementById('image-watermark-app');
if (root) {
  mountImageBatch({
    rootId: 'image-watermark-app',
    fields,
    zipName: 'watermarked-images.zip',
    dropTitle: t('اسحب الصور هنا أو انقر لاختيارها لإضافة العلامة المائية', 'Drop images here or click to choose for watermarking'),
    dropSubtitle: t('دعم الدفعات المتعددة · علامات مائية مخصصة فائقة الدقة ومعالجة محلية 100%', 'Batch support · Custom high-precision watermarks & 100% private local processing'),
    previewTitle: t('المحرر والمعاينة الحية', 'Editor & Live Preview'),
    previewSubtitle: t('اسحب العلامة المائية بحرية فوق الصورة أو اختر موضعها من الشبكة', 'Drag watermark freely on image or snap to anchor grid'),
    renderPreview: ({ canvas, ctx, bitmap, settings, statsEl }) => {
      const origW = bitmap.width;
      const origH = bitmap.height;

      const containerW = canvas.parentElement?.clientWidth || 720;
      const maxStageW = Math.min(760, containerW - 24);
      const maxStageH = 460;
      const scale = Math.min(maxStageW / origW, maxStageH / origH, 1.0);
      const displayW = Math.max(64, Math.round(origW * scale));
      const displayH = Math.max(48, Math.round(origH * scale));

      canvas.width = displayW;
      canvas.height = displayH;
      canvas.style.width = `${displayW}px`;
      canvas.style.height = `${displayH}px`;

      ctx.drawImage(bitmap, 0, 0, displayW, displayH);

      const wmBounds = drawWatermarkOnContext({
        ctx,
        width: displayW,
        height: displayH,
        settings,
        isPreview: true
      });

      currentWatermarkBounds = wmBounds;

      // Draw aesthetic interactive drag bounding frame on preview canvas (when not in tiled pattern)
      if (wmBounds && !wmBounds.isTile && wmBounds.width > 0 && wmBounds.height > 0) {
        ctx.save();
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 3]);
        ctx.strokeRect(wmBounds.x - 3, wmBounds.y - 3, wmBounds.width + 6, wmBounds.height + 6);

        // 4 Corner grip handles
        ctx.fillStyle = '#3b82f6';
        const handleSize = 5;
        const cornerPoints = [
          [wmBounds.x - 3, wmBounds.y - 3],
          [wmBounds.x + wmBounds.width + 3, wmBounds.y - 3],
          [wmBounds.x - 3, wmBounds.y + wmBounds.height + 3],
          [wmBounds.x + wmBounds.width + 3, wmBounds.y + wmBounds.height + 3]
        ];
        for (const [cx, cy] of cornerPoints) {
          ctx.fillRect(cx - handleSize / 2, cy - handleSize / 2, handleSize, handleSize);
        }

        // Mini floating drag badge
        const badgeText = imageIsAr ? '✥ اسحب بحرية' : '✥ Drag freely';
        ctx.font = '600 11px system-ui, -apple-system, sans-serif';
        const badgeW = Math.round(ctx.measureText(badgeText).width + 16);
        const badgeH = 20;
        const badgeX = Math.max(4, Math.min(displayW - badgeW - 4, Math.round(wmBounds.x + (wmBounds.width - badgeW) / 2)));
        const badgeY = wmBounds.y - 24 >= 4 ? wmBounds.y - 24 : wmBounds.y + wmBounds.height + 8;

        ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') {
          ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 10);
        } else {
          ctx.rect(badgeX, badgeY, badgeW, badgeH);
        }
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.direction = 'ltr';
        ctx.fillText(badgeText, badgeX + badgeW / 2, badgeY + badgeH / 2);
        ctx.restore();
      }

      if (statsEl) {
        const isLogo = settings.wmType === 'image';
        const posLabel = settings.position === 'tile'
          ? (imageIsAr ? 'تكرار قطري' : 'Tiled Pattern')
          : settings.position === 'custom'
          ? (imageIsAr ? 'موضع حر (سحب يدوي)' : 'Custom Position')
          : (settings.position || 'bottom_right');
        statsEl.textContent = `${origW} × ${origH} px · ${isLogo ? (imageIsAr ? 'شعار لوجو' : 'Logo') : (imageIsAr ? 'علامة نصية' : 'Text')} · ${posLabel}`;
      }
    },
    onPreviewPointer: ({ stageWrap, canvasEl, getActiveItem, getBitmap, getSettings, setSetting, drawPreview, getQueueLength, notifyItemChanged }) => {
      let isDragging = false;
      let didDrag = false;
      let dragOffset = { x: 0, y: 0 };
      let lastKnownBounds = null;

      const getPointerPos = (e) => {
        const rect = canvasEl.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        const scaleX = canvasEl.width / rect.width;
        const scaleY = canvasEl.height / rect.height;
        return {
          x: (e.clientX - rect.left) * scaleX,
          y: (e.clientY - rect.top) * scaleY
        };
      };

      const isInsideWatermark = (pos) => {
        const bounds = currentWatermarkBounds;
        if (!bounds || bounds.isTile) return false;
        const hitMargin = 16;
        return (
          pos.x >= bounds.x - hitMargin &&
          pos.x <= bounds.x + bounds.width + hitMargin &&
          pos.y >= bounds.y - hitMargin &&
          pos.y <= bounds.y + bounds.height + hitMargin
        );
      };

      stageWrap.addEventListener('pointermove', (e) => {
        const pos = getPointerPos(e);
        if (!pos) return;

        if (isDragging) {
          const bounds = currentWatermarkBounds || lastKnownBounds;
          if (!bounds) return;

          let newX = pos.x - dragOffset.x;
          let newY = pos.y - dragOffset.y;

          newX = Math.max(0, Math.min(canvasEl.width - bounds.width, newX));
          newY = Math.max(0, Math.min(canvasEl.height - bounds.height, newY));

          const customX = newX / canvasEl.width;
          const customY = newY / canvasEl.height;
          didDrag = true;

          setSetting('customX', customX);
          setSetting('customY', customY);
          setSetting('position', 'custom');

          const activeItem = getActiveItem();
          if (activeItem) {
            activeItem.settings = activeItem.settings || {};
            activeItem.settings.customX = customX;
            activeItem.settings.customY = customY;
            activeItem.settings.position = 'custom';
            const queueLen = typeof getQueueLength === 'function' ? getQueueLength() : 1;
            activeItem._manuallyCustomized = queueLen > 1;
          }

          drawPreview();
          return;
        }

        const hit = isInsideWatermark(pos);
        stageWrap.style.cursor = hit ? 'grab' : 'default';
      });

      stageWrap.addEventListener('pointerdown', (e) => {
        const pos = getPointerPos(e);
        if (!pos) return;

        if (isInsideWatermark(pos)) {
          isDragging = true;
          didDrag = false;
          lastKnownBounds = { ...currentWatermarkBounds };
          dragOffset = {
            x: pos.x - currentWatermarkBounds.x,
            y: pos.y - currentWatermarkBounds.y
          };
          stageWrap.style.cursor = 'grabbing';
          if (stageWrap.setPointerCapture) {
            try { stageWrap.setPointerCapture(e.pointerId); } catch {}
          }
        }
      });

      const onPointerUp = (e) => {
        if (isDragging) {
          isDragging = false;
          if (stageWrap.releasePointerCapture) {
            try { stageWrap.releasePointerCapture(e.pointerId); } catch {}
          }
          const pos = getPointerPos(e);
          stageWrap.style.cursor = pos && isInsideWatermark(pos) ? 'grab' : 'default';
          drawPreview();

          if (didDrag) {
            const activeItem = getActiveItem();
            if (activeItem && typeof notifyItemChanged === 'function') {
              notifyItemChanged(activeItem);
            }
          }
        }
      };

      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    },
    process: async (file, settings, report) => {
      // ----------------------------------------------------
      // العملية 1: تطبيق العلامة المائية بالدقة الأصلية الكاملة
      // Operation 1: High-resolution watermarking
      // ----------------------------------------------------
      report(15, t('المرحلة 1: فك تشفير بيانات الصورة محلياً...', 'Stage 1: Decoding image locally...'));
      const bitmap = await openBitmap(file);
      const width = bitmap.width;
      const height = bitmap.height;

      const formatInfo = resolveFormat(file, settings.outputFormat);
      const isJpeg = formatInfo.mime === 'image/jpeg';

      report(40, t('المرحلة 1: تطبيق العلامة المائية بالدقة الأصلية الكاملة...', 'Stage 1: Applying watermark at full native resolution...'));
      const { canvas, context } = makeCanvas(width, height, isJpeg ? '#ffffff' : null);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(bitmap, 0, 0);

      // Immediately release bitmap to free large textures from RAM/GPU
      bitmap.close();

      drawWatermarkOnContext({
        ctx: context,
        width,
        height,
        settings
      });

      report(55, t('اكتملت المرحلة 1 · جارٍ الانتقال لمرحلة الضغط...', 'Stage 1 complete · Transitioning to compression...'));

      // Crucial: Yield thread between stages so browser can perform GC and keep UI 100% responsive
      await nextFrame();

      // ----------------------------------------------------
      // العملية 2: ضغط وتحسين الصورة مع الحفاظ على الصيغة الأصلية وجودة 100%
      // Operation 2: Compression & optimization preserving format & 100% quality
      // ----------------------------------------------------
      report(70, t('المرحلة 2: جارٍ ضغط وتحسين الصورة بالصيغة الأصلية...', 'Stage 2: Compressing & optimizing in original format...'));

      const userQuality = Math.min(1.0, Math.max(0.1, (Number(settings.quality) || 100) / 100));
      let blob;

      if (formatInfo.mime === 'image/png') {
        report(82, t('المرحلة 2: ضغط محتوى PNG بأعلى جودة ودقة بدون فقدان...', 'Stage 2: Lossless PNG compression...'));
        try {
          const imgData = context.getImageData(0, 0, width, height);
          // If quality >= 0.95, use lossless UPNG (cnum = 0: 100% bit-exact pixel preservation)
          const cnum = userQuality >= 0.95 ? 0 : Math.max(32, Math.min(256, Math.round(userQuality * 256)));
          const pngBuffer = UPNG.encode([imgData.data.buffer], width, height, cnum);
          blob = new Blob([pngBuffer], { type: 'image/png' });
        } catch (pngErr) {
          console.warn('UPNG encoding failed, falling back to canvasToBlob:', pngErr);
          blob = await canvasToBlob(canvas, 'image/png');
        }
      } else if (formatInfo.mime === 'image/webp') {
        report(82, t('المرحلة 2: ضغط WebP بجودة فائقة...', 'Stage 2: Compressing WebP with high quality...'));
        blob = await canvasToBlob(canvas, 'image/webp', userQuality);
      } else {
        // Default to JPEG
        report(82, t('المرحلة 2: ضغط وتحسين JPEG بجودة متقنة...', 'Stage 2: Compressing & optimizing JPEG...'));
        blob = await canvasToBlob(canvas, 'image/jpeg', userQuality);

        // Smart guard against inflation: If output JPEG is larger than original by > 15%, test high-fidelity 0.90
        if (blob.size > file.size * 1.15 && userQuality >= 0.92) {
          const optBlob = await canvasToBlob(canvas, 'image/jpeg', 0.90);
          if (optBlob.size < blob.size) {
            blob = optBlob;
          }
        }
      }

      // Yield frame again before finishing packaging
      await nextFrame();
      report(100, t('اكتملت إضافة العلامة المائية والضغط بنجاح', 'Watermarking and compression completed'));

      trackEvent('tool_completed', 'image-watermark', {
        input_bytes: file.size,
        output_bytes: blob.size,
        format: formatInfo.ext
      });

      return {
        blob,
        filename: `${imageName(file.name)}.${formatInfo.ext}`,
        details: `${width} × ${height} px · ${formatInfo.ext.toUpperCase()}`
      };
    }
  });
}
